import React from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  Skeleton,
  useTheme,
  alpha,
  Chip,
} from '@mui/material';
import { TrendingUp, TrendingDown, TrendingFlat } from '@mui/icons-material';

export interface MetricCardProps {
  title: string;
  value: string | number;
  icon: React.ReactNode;
  trend?: {
    value: number;
    direction: 'up' | 'down' | 'flat';
  };
  subtitle?: string;
  color?: 'primary' | 'secondary' | 'success' | 'error' | 'warning' | 'info';
  loading?: boolean;
  onClick?: () => void;
}

const MetricCard: React.FC<MetricCardProps> = React.memo(({
  title,
  value,
  icon,
  trend,
  subtitle,
  color = 'primary',
  loading = false,
  onClick,
}) => {
  const theme = useTheme();

  const getTrendIcon = () => {
    if (!trend) return undefined;
    switch (trend.direction) {
      case 'up':
        return <TrendingUp fontSize="small" />;
      case 'down':
        return <TrendingDown fontSize="small" />;
      default:
        return <TrendingFlat fontSize="small" />;
    }
  };

  const getTrendColor = () => {
    if (!trend) return 'default';
    return trend.direction === 'up' ? 'success' : trend.direction === 'down' ? 'error' : 'default';
  };

  if (loading) {
    return (
      <Card sx={{ height: '100%', cursor: onClick ? 'pointer' : 'default' }}>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
            <Skeleton variant="circular" width={40} height={40} />
            <Skeleton variant="text" width={60} />
          </Box>
          <Skeleton variant="text" width="60%" height={32} />
          <Skeleton variant="text" width="40%" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card
      sx={{
        height: '100%',
        cursor: onClick ? 'pointer' : 'default',
        position: 'relative',
        overflow: 'hidden',
        background: `linear-gradient(135deg, ${alpha(theme.palette[color].main, 0.02)} 0%, ${alpha(theme.palette[color].main, 0.08)} 100%)`,
        border: `1px solid ${alpha(theme.palette[color].main, 0.1)}`,
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: '3px',
          background: `linear-gradient(90deg, ${theme.palette[color].main}, ${theme.palette[color].light})`,
        },
      }}
      onClick={onClick}
    >
      <CardContent sx={{ pt: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2.5 }}>
          <Box
            sx={{
              backgroundColor: alpha(theme.palette[color].main, 0.12),
              borderRadius: 3,
              p: 1.5,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: `0 4px 12px ${alpha(theme.palette[color].main, 0.15)}`,
            }}
          >
            <Box sx={{
              color: theme.palette[color].main,
              display: 'flex',
              alignItems: 'center',
            }}>
              {icon}
            </Box>
          </Box>
          {trend && (
            <Chip
              size="small"
              icon={getTrendIcon()}
              label={`${trend.value}%`}
              color={getTrendColor()}
              variant="outlined"
            />
          )}
        </Box>
        <Typography
          gutterBottom
          variant="body2"
          sx={{
            color: 'text.secondary',
            fontWeight: 500,
            textTransform: 'uppercase',
            fontSize: '0.75rem',
            letterSpacing: '0.5px'
          }}>
          {title}
        </Typography>
        <Typography
          variant="h3"
          component="div"
          sx={{
            mb: subtitle ? 1 : 0,
            fontWeight: 700,
            color: theme.palette.text.primary,
          }}
        >
          {value.toLocaleString()}
        </Typography>
        {subtitle && (
          <Typography variant="caption" sx={{
            color: 'text.secondary'
          }}>
            {subtitle}
          </Typography>
        )}
      </CardContent>
    </Card>
  );
});

MetricCard.displayName = 'MetricCard';

export default MetricCard;
